import { useEffect, useRef, useState } from 'react'
import { Monitor, RefreshCw, Maximize2, Minimize2, Pause, Play, Radio } from 'lucide-react'
import { useBrowserLive, useBrowserLiveVideo } from '@/hooks/useApi'

const profiles = {
  smooth: { quality: 25, maxWidth: 640, maxHeight: 400 },
  balanced: { quality: 60, maxWidth: 1280, maxHeight: 800 },
  detail: { quality: 80, maxWidth: 1920, maxHeight: 1200 },
}
type Quality = keyof typeof profiles
type StreamState = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'paused'
type Stats = { fps: number; skipped: number; width: number; height: number; lastFrame: number }

function useStream(url: string | null | undefined, canvas: React.RefObject<HTMLCanvasElement | null>, quality: Quality, paused: boolean, retry: number) {
  const [state, setState] = useState<StreamState>('idle')
  const [error, setError] = useState('')
  const [stats, setStats] = useState<Stats>({ fps: 0, skipped: 0, width: 0, height: 0, lastFrame: 0 })
  useEffect(() => {
    setError('')
    setStats({ fps: 0, skipped: 0, width: 0, height: 0, lastFrame: 0 })
    if (paused || !url) { setState(paused ? 'paused' : 'idle'); return }
    let disposed = false, socket: WebSocket | null = null, timer: ReturnType<typeof setTimeout> | undefined
    let generation = 0, id = 0, attempts = 0, rendered = 0, skipped = 0, lastFrame = 0
    let width = 0, height = 0, drawRequest = 0
    const connect = () => {
      if (disposed) return
      const currentGeneration = ++generation
      setState(attempts ? 'reconnecting' : 'connecting')
      const ws = new WebSocket(url)
      socket = ws
      let pending: string | null = null, decoding = false
      const valid = () => !disposed && generation === currentGeneration
      const send = (method: string, params = {}) => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ id: ++id, method, params }))
      }
      // One decoder and one waiting frame: slow devices never build an image queue.
      const decode = () => {
        if (!valid() || decoding || !pending) return
        const data = pending
        pending = null
        decoding = true
        const img = new Image()
        img.onload = () => {
          if (!valid()) return
          drawRequest = requestAnimationFrame(() => {
          if (!valid()) return;
          const target = canvas.current
          if (target) {
            if (target.width !== img.width || target.height !== img.height) {
              target.width = img.width; target.height = img.height
            }
            target.getContext('2d')?.drawImage(img, 0, 0)
            width = img.width; height = img.height
            rendered++; lastFrame = Date.now()
            setState('live')
          }
          decoding = false
          decode()
          })
        }
        img.onerror = () => { if (valid()) { decoding = false; skipped++; decode() } }
        img.src = 'data:image/jpeg;base64,' + data
      }
      ws.onopen = () => {
        if (!valid()) return
        attempts = 0; setError('')
        send('Page.enable')
        send('Page.startScreencast', { format: 'jpeg', ...profiles[quality], everyNthFrame: 1 })
      }
      ws.onmessage = event => {
        if (!valid()) return
        let message
        try { message = JSON.parse(event.data) } catch { return }
        if (message.error) { setError('The browser could not start the video stream. Try reconnecting.'); return }
        if (message.method !== 'Page.screencastFrame') return
        const { data, sessionId } = message.params || {}
        // Acknowledge promptly even when replacing a queued frame.
        send('Page.screencastFrameAck', { sessionId })
        if (typeof data !== 'string') return
        if (pending) skipped++
        pending = data; decode()
      }
      ws.onerror = () => { if (valid()) setError('Video connection interrupted. Retrying…') }
      ws.onclose = () => {
        if (!valid()) return
        generation++; pending = null; setState('reconnecting')
        timer = setTimeout(connect, Math.min(10000, 1000 * 2 ** Math.min(attempts++, 3)))
      }
    }
    const ticker = setInterval(() => {
      setStats({ fps: rendered, skipped, width, height, lastFrame }); rendered = 0
    }, 1000)
    connect()
    return () => {
      disposed = true; generation++
      clearTimeout(timer); clearInterval(ticker); cancelAnimationFrame(drawRequest)
      if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ id: ++id, method: 'Page.stopScreencast' }))
      socket?.close()
    }
  }, [url, canvas, quality, paused, retry])
  return { state, stats, error }
}

export function BrowserLivePanel() {
  const { data, isLoading, refetch, isFetching } = useBrowserLive()
  const { data: video } = useBrowserLiveVideo(!!data?.active)
  const canvas = useRef<HTMLCanvasElement>(null)
  const viewer = useRef<HTMLDivElement>(null)
  const [quality, setQuality] = useState<Quality>('smooth')
  const [size, setSize] = useState('large')
  const [paused, setPaused] = useState(false)
  const [retry, setRetry] = useState(0)
  const [fullscreen, setFullscreen] = useState(false)
  const [fullscreenError, setFullscreenError] = useState('')
  const { state, stats, error } = useStream(data?.active ? video?.video_ws_url : null, canvas, quality, paused, retry)
  useEffect(() => {
    const update = () => setFullscreen(document.fullscreenElement === viewer.current)
    document.addEventListener('fullscreenchange', update)
    return () => document.removeEventListener('fullscreenchange', update)
  }, [])
  const toggleFullscreen = async () => {
    setFullscreenError('')
    try {
      if (document.fullscreenElement) await document.exitFullscreen()
      else if (viewer.current?.requestFullscreen) await viewer.current.requestFullscreen()
      else setFullscreenError('Fullscreen is unavailable in this browser. Choose Large to expand the viewer.')
    } catch { setFullscreenError('Fullscreen was unavailable. Choose Large to expand the viewer.') }
  }
  const button = 'inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-surface-elevated px-3 py-2 text-xs text-text-primary hover:opacity-80 disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-accent'
  const age = stats.lastFrame ? Math.max(0, Math.floor((Date.now() - stats.lastFrame) / 1000)) : null
  let sessionHost = ''
  try { sessionHost = data?.url ? new URL(data.url).hostname : '' } catch { /* No usable page URL yet. */ }
  const status = !data?.active ? 'No VPS session' : paused ? 'Paused' : state === 'live' ? 'Live' : state === 'reconnecting' ? 'Reconnecting' : 'Connecting'
  return <section className="space-y-4">
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-sky-400/20 bg-gradient-to-br from-sky-500/10 via-surface to-indigo-500/10 p-5 sm:p-6">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-accent">OPS · Browser workspace</p>
        <h2 className="flex items-center gap-2 text-xl font-semibold text-text-primary"><Monitor className="h-5 w-5 text-accent" /> Browser Live View</h2>
        <p className="mt-1 text-sm text-text-muted">Watch OPS work in your VPS browser.</p>
      </div>
      <span role="status" className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs text-text-primary">
        <span className={state === 'live' && data?.active ? 'h-2 w-2 rounded-full bg-emerald-400' : 'h-2 w-2 rounded-full bg-slate-400'} />{status}
      </span>
    </div>

    <div ref={viewer} className="flex flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-lg" style={fullscreen ? { height: '100dvh' } : undefined}>
      <div className="flex min-w-0 items-center gap-3 border-b border-border bg-surface-elevated px-4 py-3">
        <Monitor className="h-4 w-4 shrink-0 text-accent" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-text-primary">{data?.active ? data.title || 'VPS browser session' : 'VPS browser'}</p>
          <p className="truncate text-xs text-text-muted">{sessionHost || 'Waiting for a session'}</p>
        </div>
        <span className="shrink-0 rounded-md border border-border px-2 py-1 text-xs text-text-muted">Live preview</span>
      </div>
      <div className="flex flex-wrap items-end gap-3 border-b border-border p-3 sm:p-4">
        <label className="flex flex-col gap-1 text-xs text-text-muted">Viewer size
          <select aria-label="Viewer size" value={size} onChange={e => setSize(e.target.value)} className={button}>
            <option value="compact">Compact</option><option value="standard">Standard</option><option value="large">Large</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-muted">Stream quality
          <select aria-label="Stream quality" value={quality} onChange={e => setQuality(e.target.value as Quality)} className={button}>
            <option value="smooth">Smooth · less data</option><option value="balanced">Balanced</option><option value="detail">Sharper text</option>
          </select>
        </label>
        <div className="ml-auto flex flex-wrap gap-2">
          <button className={button} disabled={!data?.active} onClick={() => setPaused(!paused)}>{paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}{paused ? 'Resume' : 'Pause'}</button>
          <button className={button} onClick={toggleFullscreen}>{fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}{fullscreen ? 'Exit fullscreen' : 'Fullscreen'}</button>
          <button className={button} aria-label="Reconnect live view" disabled={isFetching} onClick={() => { setRetry(n => n + 1); void refetch() }}><RefreshCw className={isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} /></button>
        </div>
      </div>
      <div className="relative flex min-h-64 items-center justify-center overflow-hidden bg-[#080f1e]" style={{ height: fullscreen ? undefined : size === 'compact' ? '32dvh' : size === 'standard' ? '50dvh' : '68dvh', flex: fullscreen ? '1 1 0%' : undefined }}>
        <canvas ref={canvas} aria-label="Live VPS browser video" className="h-full w-full object-contain" style={{ visibility: data?.active && stats.lastFrame ? 'visible' : 'hidden' }} />
        {(!data?.active || !stats.lastFrame || paused || state === 'reconnecting') && <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#080f1e]/85 px-6 text-center">
          <div className="rounded-2xl border border-sky-400/20 bg-sky-400/10 p-4"><Radio className="h-8 w-8 text-sky-400" /></div>
          <p className="text-base font-medium text-slate-100">{isLoading ? 'Checking browser session…' : !data?.active ? 'Your VPS browser will appear here' : paused ? 'Viewing paused' : state === 'reconnecting' ? 'Reconnecting to the browser…' : 'Waiting for the first frame…'}</p>
          <p className="max-w-md text-sm leading-relaxed text-slate-400">{!data?.active ? 'When a VPS browser session starts, its live view appears automatically. Your physical browser connection is separate.' : paused ? 'The browser keeps working. Resume to watch again.' : 'Keep this page open to watch the session.'}</p>
        </div>}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3 text-xs text-text-muted">
        <span>View only · resizing does not change the VPS browser</span>
        {data?.active && <span>{stats.fps} FPS{stats.width ? ' · ' + stats.width + ' × ' + stats.height : ''}{age !== null && age > 2 ? ' · Last update ' + age + 's ago' : ''}</span>}
      </div>
    </div>
    {(error || data?.error || fullscreenError) && <p role="alert" className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-500">{fullscreenError || error || data?.error}</p>}
    {data?.active && <p className="text-xs text-text-muted">Frame rate follows page activity; a still page may show 0 FPS. Choose Smooth on slower connections. Changing quality briefly reconnects the viewer.</p>}
  </section>
}
